import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Avatar, Badge, Box, Divider, Tooltip, Typography } from "@mui/material";
import { LocalOffer as OfferIcon } from "../icons";
import { Event as EventIcon } from "../icons";
import { Map as MapIcon } from "../icons";
import { Forum as ForumIcon } from "../icons";
import { DirectionsCar as DirectionsCarIcon } from "../icons";
import { Flight as FlightIcon } from "../icons";
import { DeliveryDining as DeliveryDiningIcon } from "../icons";
import { Search as SearchIcon } from "../icons";
import { Chat as ChatIcon } from "../icons";
import { Notifications as NotificationsIcon } from "../icons";
import { Settings as SettingsIcon } from "../icons";
import { T } from "../routes/Translate";
import PublishSpeedDial from "../routes/PublishSpeedDial";
import { useNavUserState } from "../routes/BottomToolbar";
import { useUnreadMessageCount } from "../routes/ChatNavButton";
import { alpha } from "@mui/material/styles";
import { barColors, brandColors } from "../theme";
const LOGO = `${process.env.PUBLIC_URL}/logo.png`;

// Secciones de contenido (lo que en mobile es el AppBar de categorías,
// CustomToolbar.js) y, debajo, las acciones personales (lo que en mobile es
// la BottomToolbar). Los ids son los mismos que usa el tour de Joyride en
// esas barras (ver joyrideSteps en App.js), que en desktop no se dibujan.
const SECTIONS = [
  { to: "/home", label: "Ofertas", Icon: OfferIcon, id: "tour-etiqueta", match: ["/home", "/"] },
  { to: "/events", label: "Eventos", Icon: EventIcon, id: "tour-calendario" },
  { to: "/cars", label: "Autos", Icon: DirectionsCarIcon, id: "tour-auto" },
  { to: "/trips", label: "Lugares", Icon: FlightIcon, id: "tour-avion" },
  { to: "/pedidos", label: "Pedidos", Icon: DeliveryDiningIcon },
  { to: "/map", label: "Mapa", Icon: MapIcon, id: "tour-map" },
  { to: "/hilos", label: "Hilos", Icon: ForumIcon },
];

const isActivePath = (pathname, item) => {
  const paths = item.match || [item.to];
  return paths.some((p) => (p === "/" ? pathname === "/" : pathname === p || pathname.startsWith(`${p}/`)));
};

const NavLinkItem = ({ to, label, icon, id, active, collapsed }) => {
  const link = (
    <Box
      component={Link}
      to={to}
      id={id}
      aria-label={collapsed ? label : undefined}
      aria-current={active ? "page" : undefined}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: collapsed ? "center" : "flex-start",
        gap: 2,
        height: 44,
        px: collapsed ? 0 : 1.75,
        mx: collapsed ? "auto" : 0,
        width: collapsed ? 44 : "auto",
        position: "relative",
        borderRadius: 999,
        color: barColors.text,
        textDecoration: "none",
        backgroundColor: active ? barColors.activeBg : "transparent",
        transition: "background-color 0.15s ease",
        // Hover más suave que la sección activa, para no confundirlos.
        "&:hover": { backgroundColor: active ? barColors.activeBg : alpha(barColors.text, 0.06) },
        "& .MuiSvgIcon-root": { color: barColors.icon },
        // Indicador de la sección actual: barra de color de marca pegada al
        // borde izquierdo de la sidebar. Abierta: el ítem arranca después
        // del px de la nav (12px). Colapsada (SIDEBAR_COLLAPSED_WIDTH 76): px
        // 8px + el ítem de 44px centrado en los 59px que quedan = 15.5px.
        "&::before": {
          content: '""',
          position: "absolute",
          left: collapsed ? -15.5 : -12,
          top: "50%",
          width: 4,
          height: active ? 24 : 0,
          borderRadius: "0 4px 4px 0",
          backgroundColor: brandColors.accent,
          transform: "translateY(-50%)",
          transition: "height 0.15s ease",
        },
      }}
    >
      {icon}
      {!collapsed && (
        <Typography noWrap sx={{ fontSize: 15, fontWeight: active ? 700 : 500 }}>
          <T>{label}</T>
        </Typography>
      )}
    </Box>
  );

  return collapsed ? (
    <Tooltip title={<T>{label}</T>} placement="right">
      {link}
    </Tooltip>
  ) : (
    link
  );
};

// Navegación de la versión web de escritorio: reemplaza al AppBar de
// categorías y a la BottomToolbar. App.js decide cuándo se muestra (ver
// sidebarActive) y le reserva el ancho a la izquierda del contenido.
const DesktopSidebar = ({ width, collapsed }) => {
  const { pathname } = useLocation();
  const { hasNewNotifications, profileImageUrl } = useNavUserState();
  const unreadMessageCount = useUnreadMessageCount();

  const personal = [
    { to: "/members", label: "Buscar", icon: <SearchIcon />, id: "tour-search" },
    {
      to: "/chats",
      label: "Chats",
      id: "tour-chats-btn",
      icon: (
        <Badge badgeContent={unreadMessageCount} color="error" invisible={unreadMessageCount === 0}>
          <ChatIcon />
        </Badge>
      ),
    },
    {
      to: "/notifications",
      label: "Notificaciones",
      id: "tour-notifications",
      icon: (
        <Badge variant="dot" color="error" invisible={!hasNewNotifications}>
          <NotificationsIcon />
        </Badge>
      ),
    },
    { to: "/settings", label: "Ajustes", icon: <SettingsIcon /> },
  ];

  return (
    <Box
      component="nav"
      aria-label="Navegación principal"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        bottom: 0,
        width,
        zIndex: (theme) => theme.zIndex.appBar + 1,
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        px: collapsed ? 1 : 1.5,
        py: 2,
        backgroundColor: barColors.background,
        color: barColors.text,
        borderRight: `1px solid ${barColors.border}`,
        overflowY: "auto",
        overflowX: "hidden",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      <Box
        component={Link}
        to="/home"
        aria-label="Notiline"
        sx={{ display: "flex", justifyContent: collapsed ? "center" : "flex-start", px: collapsed ? 0 : 1.75, mb: 2.5, minHeight: 36, alignItems: "center", textDecoration: "none" }}
      >
        <Box
          component="img"
          src={LOGO}
          alt="Notiline"
          sx={{ width: collapsed ? 56 : 136, height: "auto", aspectRatio: "272 / 96", borderRadius: 1.5, display: "block" }}
        />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {SECTIONS.map(({ Icon, ...item }) => (
          <NavLinkItem
            key={item.to}
            {...item}
            icon={<Icon />}
            active={isActivePath(pathname, item)}
            collapsed={collapsed}
          />
        ))}
      </Box>

      <Divider sx={{ my: 1.5, borderColor: barColors.border }} />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {personal.map((item) => (
          <NavLinkItem key={item.to} {...item} active={isActivePath(pathname, item)} collapsed={collapsed} />
        ))}
      </Box>

      <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
        <PublishSpeedDial variant="sidebar" collapsed={collapsed} />
      </Box>

      <Box sx={{ flexGrow: 1 }} />

      <NavLinkItem
        to="/profile"
        label="Perfil"
        id="tour-profile"
        active={isActivePath(pathname, { to: "/profile" })}
        collapsed={collapsed}
        icon={
          <Avatar
            src={profileImageUrl || undefined}
            sx={{ width: 28, height: 28, border: `2px solid ${barColors.icon}` }}
          />
        }
      />
    </Box>
  );
};

export default DesktopSidebar;
